"use client"

/*
 * DEV HANDOFF - social-settings.tsx
 *
 * A DESTINATION'S OWN SETTINGS - the redesign of
 * `Your Socials > [account] > Global Settings`.
 *
 * Same three files, same shell, as `feed-settings.tsx`:
 *   settings-page.tsx        the shell
 *   settings-form.tsx        the divided settings list
 *   social-settings.data.ts  the field spec - MOCK DATA, does not ship
 *
 * THERE ARE TWO LEVELS OF DESTINATION SETTINGS AND THIS IS THE OUTER ONE.
 * These are `social.params`: they apply to every automation posting to this
 * destination, which is what the word "Global" in the title is carrying and
 * why it must not be renamed away.
 *
 * ONE AUTOMATION'S OVERRIDE of them is its own screen - `output.paParams`, a
 * modal in prod, legacy's `OutputConfigModal` titled "Settings for {account}".
 * See `destination-override.tsx`. It was a `scope` prop on this component and
 * is now separate: different record, different surface, reached from a
 * different place, so one component serving both was hiding three differences
 * behind one flag.
 *
 * THE TAB SET IS PER NETWORK and resolves to three outcomes, not three tabs:
 * X / LinkedIn / StockTwits get Post options + Links + Advanced, Pinterest /
 * TikTok / YouTube get Post options ALONE, everything else gets Post options +
 * Links. `buildSocialSections()` carries the rule; the shell hides the strip
 * entirely when there is only one section, because one tab is not a choice.
 *
 * WIRING (production owns all of it):
 * - `SocialEditor.jsx` keeps state, the deep-equal dirty diff, save / cancel and
 *   the ErrorModule plumbing; only the panel body is replaced. The seam is the
 *   `this.props.tile` dual-render flag `FacebookPageConfigPanel.jsx` already uses.
 * - `sections` is the whole data contract. Build it from the social record;
 *   every field carries its legacy `data-field` dot path in `SettingsField.name`.
 * - `SocialEditorPanel.jsx`'s `switch (psID)` into 21 hand-written
 *   `*Options.jsx` files becomes per-network spec data, the way
 *   `ui/src/lib/social-input-configure.ts` already does it for the wizard.
 *
 * COPY: labels are sentence-cased per the locked copy rules; see HANDOFF.md for
 * the before/after list and the open vocabulary question on "destination".
 */

import * as React from "react"
import { History } from "lucide-react"

import {
  DESTINATION_SLUG,
  NETWORK_LABEL,
  type DestinationNetwork,
} from "@/shadcn/components/settings/settings-vocab"
import { type SettingsSection } from "@/shadcn/components/settings/settings-form"
import {
  SettingsDeleteDialog,
  SettingsPage,
  type SettingsPageProps,
  SettingsAvatar,
} from "@/shadcn/components/settings/settings-page"

type SocialSettingsProps = Partial<SettingsPageProps> & {
  /**
   * REQUIRED HERE, unlike the preview kit, where it defaults to a mock
   * fixture. Prod builds this from the stored record; there is no fixture to
   * fall back to. See `settings-vocab.ts` for why the split exists.
   */
  sections: SettingsSection[]
  /** Drives the default meta line. The tab set comes from `sections`. */
  network?: DestinationNetwork
  /** The connected account, e.g. "samsamspdx". */
  accountName?: string
  /**
   * The picture the legacy avatar shows: `input.icon` for a source,
   * `social.icon` (else `social.src`) for a destination. Omitted, or failing
   * to load, the avatar falls back to initials.
   */
  avatarUrl?: string
}

export default function SocialSettings({
  sections,
  network = "twitter",
  accountName = "samsamspdx",
  avatarUrl,
  activeSection,
  title,
  meta,
  backLabel,
  note,
  actions,
  ...props
}: SocialSettingsProps) {
  /*
   * THE NOTE IS HIDDEN ON THE LINKS TAB, and that is legacy's rule, not a
   * preference: `SocialEditor.jsx:201` wraps the banner in
   * `if (!pathname.match("links"))`. The reason is that it would be false
   * there - the shortener and the analytics string apply to EVERY link this
   * destination posts, automated or not, so "only apply to automated posts"
   * would be telling the user the opposite of the truth on that one tab.
   *
   * The shell renders the note above the strip rather than per tab, so the
   * visible section has to be resolved here. Same fallback the shell uses, so
   * the two cannot disagree about which tab is showing.
   */
  const shownSection =
    activeSection && sections.some((s) => s.id === activeSection)
      ? activeSection
      : sections[0]?.id
  const onLinksTab = shownSection === "links"

  return (
    <SettingsPage
      sections={sections}
      /*
       * The name leads and the settings noun sits under it (Emily,
       * 2026-10-01): the reader arrives knowing which destination they opened, so
       * that is what the big line confirms. Same type as before, swapped.
       */
      title={title ?? accountName}
      icon={
        <SettingsAvatar
          name={accountName}
          avatarUrl={avatarUrl}
          network={DESTINATION_SLUG[network]}
        />
      }
      meta={meta ?? `Global settings · ${NETWORK_LABEL[network]}`}
      backLabel={backLabel ?? "Outputs"}
      note={
        /*
         * `undefined` MEANS "NOT SPECIFIED", `null` MEANS "NONE". `??` cannot
         * tell the two apart, and a dismissal passes null — so under `??` the
         * note came straight back. `SettingsPageProps` states the contract;
         * this is the explicit check that keeps it.
         */
        note !== undefined
          ? note
          : onLinksTab
            ? null
            : /* Legacy's own sentence. The other two facts it used to carry -
                 that these span every automation, and that an automation can
                 override them - are left to the "Global settings" title and
                 the per-row Override badge rather than spent here. */
              "These settings only apply to automated posts."
      }
      actions={actions ?? [{ label: "Error log", icon: <History /> }]}
      activeSection={activeSection}
      {...props}
      /* No delete card here (Emily, 2026-10-01): deleting lives on the list
         this screen was opened from. Set after the spread so a caller cannot
         turn it back on. Complex keeps its own on Source settings. */
      showDelete={false}
    />
  )
}

/**
 * "Delete this destination?" - no Figma frame; built as a clone of the
 * automation confirm (`29890:1501`) so the two deletes are one object. Needs
 * drawing back. Surface only; drop it into `AlertDialogContent` in prod with
 * the `.tw-root` portal patch.
 */
export function DeleteDestinationDialog({
  accountName = "samsamspdx",
  ...props
}: Omit<
  React.ComponentProps<typeof SettingsDeleteDialog>,
  "title" | "description" | "confirmLabel"
> & { accountName?: string }) {
  return (
    <SettingsDeleteDialog
      title="Delete this output?"
      description={`We will disconnect ${accountName} and remove it from every automation posting to it. Posts we already published stay up. This cannot be undone.`}
      confirmLabel="Delete output"
      {...props}
    />
  )
}
